import { useState } from 'react';
import { Avatar } from '../../components/ui';
import type { ConsoleAlert, ConsoleAlerts as Alerts } from './huddleApi';
import { huddleCommentHtml, huddleCommentTime } from './huddlePresentation';

/** Design 4d [R8]: the excerpt is built in the browser and shown as text. */
function excerpt(body: string): string {
  const holder = document.createElement('template');
  holder.innerHTML = huddleCommentHtml(body);
  const text = (holder.content.textContent ?? '').replace(/\s+/g, ' ').trim();
  return text.length > 120 ? `${text.slice(0, 119)}…` : text;
}

export function ConsoleAlerts({
  alerts,
  ready,
  onItem,
  onMarkAll,
  open: controlledOpen,
  onOpenChange,
  disabled,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  alerts: Alerts;
  /** False until an alerts load succeeds, and again after a failed one. */
  ready: boolean;
  onItem: (item: ConsoleAlert) => void;
  onMarkAll: () => void;
}) {
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlledOpen ?? localOpen;
  const setOpen = onOpenChange ?? setLocalOpen;
  const count = ready ? alerts.total : 0;
  const shown = count === 99 ? '99+' : String(count);
  return (
    <div className="console-alerts">
      <button
        className="console-bell"
        disabled={disabled}
        type="button"
        aria-label={count ? `Notifications, ${shown} new` : 'Notifications'}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span aria-hidden="true">🔔</span>
        {count ? <span className="console-bell-count">{shown}</span> : null}
      </button>
      {open ? (
        <section className="console-alert-list" aria-label="What's new">
          <header>
            <h2>What's new</h2>
            {ready && alerts.items.length > 0 ? (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onMarkAll();
                }}
              >
                Mark all read
              </button>
            ) : null}
          </header>
          {!ready || alerts.items.length === 0 ? (
            <p className="console-alert-empty">You're all caught up.</p>
          ) : (
            alerts.items.map((item) => (
              <button
                type="button"
                className="console-alert-row"
                key={item.comment_id}
                onClick={() => {
                  setOpen(false);
                  onItem(item);
                }}
              >
                <Avatar name={item.author} imageUrl={null} size="sm" />
                <span className="console-alert-text">
                  <strong>
                    {item.kind === 'message'
                      ? `${item.author} sent you a message`
                      : `${item.author} commented on ${item.task_title ?? 'a task'}`}
                  </strong>
                  <span className="console-alert-excerpt">{excerpt(item.body)}</span>
                  <time dateTime={item.created_at}>{huddleCommentTime(item.created_at)}</time>
                </span>
              </button>
            ))
          )}
        </section>
      ) : null}
    </div>
  );
}
